import ChalkySticks from '@chalkysticks/sdk';
import State from './State';

/**
 * Mutations are a synchronous way of modifying state. They are generally
 * called through Actions. It's best to think of them as setters.
 *
 * @see https://vuex.vuejs.org/guide/mutations.html
 *
 * @class Mutations
 * @package Store/Module/Location
 * @project ChalkySticks App
 */
const mutations = {
	/**
	 * Sets the user's position from a GeolocationPosition object
	 *
	 * @param typeof State state
	 * @param GeolocationPosition position
	 * @return void
	 */
	setPosition(state: typeof State, position: GeolocationPosition): void {
		// Check that we have coordinates to work with
		if (!position || !position.coords) {
			ChalkySticks.Utility.Log.warn('store-location', 'No coordinates found in position');
			return;
		}

		// Validate that coordinates are actual numbers
		const { latitude, longitude } = position.coords;

		if (typeof latitude !== 'number' || typeof longitude !== 'number' || isNaN(latitude) || isNaN(longitude)) {
			ChalkySticks.Utility.Log.warn('store-location', 'Invalid coordinate values', latitude, longitude);
			return;
		}

		// Validate coordinate ranges
		if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
			ChalkySticks.Utility.Log.warn('store-location', 'Coordinates out of valid range', latitude, longitude);
			return;
		}

		const coordinates = ChalkySticks.Utility.Geolocation.simplifyCoordinates(position.coords, 5);

		// Validate simplified coordinates
		if (!coordinates || typeof coordinates.latitude !== 'number' || typeof coordinates.longitude !== 'number') {
			ChalkySticks.Utility.Log.error('store-location', 'Coordinate simplification failed');
			return;
		}

		state.latitude = coordinates.latitude;
		state.longitude = coordinates.longitude;
		state.timestamp = position.timestamp;

		ChalkySticks.Utility.Log.debug('store-location', 'Saving location', state.latitude, state.longitude);
	},
};

export default mutations;
